import {
  type EnvironmentProviders,
  Injectable,
  inject,
  provideAppInitializer,
  signal,
} from '@angular/core';

/**
 * Settings that differ per deployment and are read from `assets/config.json` before the app
 * bootstraps. The file is served as a static asset, so the same build runs in every
 * environment and an operator changes behaviour by replacing one file.
 *
 * Nothing secret belongs here: the file is public. The company is never configured here
 * either; it comes from the signed-in session.
 */
export interface RuntimeConfig {
  /** Origin of the API with no trailing slash. Empty means same origin (the dev proxy). */
  readonly apiBaseUrl: string;
  /**
   * Use the in-memory sign-in instead of the real API. Development only. A production build
   * refuses to start with this on (see `provideAuthApi`).
   */
  readonly mockApi: boolean;
  /** IANA zone used for dates until the signed-in company supplies its own. */
  readonly defaultTimeZone: string;
  /** Name of the CSRF cookie the API sets. */
  readonly csrfCookie: string;
  /** Header the API expects the CSRF token in. */
  readonly csrfHeader: string;
  /**
   * The bot API HRM Buddy talks to, with no trailing slash. Empty means the assistant
   * answers from its built-in help guide instead. Moving the chat to another server is this one
   * setting. A runtime override is deliberately not offered, so a crafted link cannot redirect
   * people's questions to another server.
   */
  readonly botApiUrl: string;
  /** Identifies this site to the bot API. */
  readonly botSiteId: string;
  /** Public key that identifies this site (not a secret). Supplied per environment. */
  readonly botSiteKey: string;
}

export const DEFAULT_RUNTIME_CONFIG: RuntimeConfig = {
  apiBaseUrl: '',
  mockApi: false,
  defaultTimeZone: 'Asia/Dhaka',
  csrfCookie: 'hrm_csrf',
  csrfHeader: 'X-CSRF',
  botApiUrl: '',
  botSiteId: 'hrm',
  botSiteKey: '',
};

function text(value: unknown, fallback: string): string {
  return typeof value === 'string' ? value : fallback;
}

/**
 * Builds a config from untyped JSON. A missing or wrongly typed key takes its default, so a
 * partial file cannot leave `undefined` reaching a request. Unknown keys are dropped.
 */
export function parseRuntimeConfig(raw: unknown): RuntimeConfig {
  const source = typeof raw === 'object' && raw !== null ? (raw as Record<string, unknown>) : {};
  const d = DEFAULT_RUNTIME_CONFIG;
  return {
    apiBaseUrl: text(source['apiBaseUrl'], d.apiBaseUrl).replace(/\/+$/, ''),
    mockApi: source['mockApi'] === true,
    defaultTimeZone: text(source['defaultTimeZone'], d.defaultTimeZone) || d.defaultTimeZone,
    csrfCookie: text(source['csrfCookie'], d.csrfCookie) || d.csrfCookie,
    csrfHeader: text(source['csrfHeader'], d.csrfHeader) || d.csrfHeader,
    botApiUrl: text(source['botApiUrl'], d.botApiUrl).replace(/\/+$/, ''),
    botSiteId: text(source['botSiteId'], d.botSiteId) || d.botSiteId,
    botSiteKey: text(source['botSiteKey'], d.botSiteKey),
  };
}

/** Holds the loaded runtime config. `load()` runs once, before bootstrap completes. */
@Injectable({ providedIn: 'root' })
export class RuntimeConfigService {
  private readonly state = signal<RuntimeConfig>(DEFAULT_RUNTIME_CONFIG);
  readonly config = this.state.asReadonly();

  /**
   * Fetches the config file with `fetch`, not `HttpClient`, so no interceptor (which itself
   * needs this config) is involved. If the file cannot be read the safe defaults apply:
   * same-origin API, no mock, Dhaka time.
   */
  async load(url = 'assets/config.json'): Promise<void> {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const base: unknown = await response.json();
      const local = await this.loadLocal(url.replace(/config\.json$/, 'config.local.json'));
      this.state.set(parseRuntimeConfig({ ...(base as object), ...local }));
    } catch (error) {
      console.warn(`Runtime config could not be read from ${url}; using defaults.`, error);
      this.state.set(DEFAULT_RUNTIME_CONFIG);
    }
  }

  /**
   * An optional second file for values that must stay out of the repository, such as the bot site
   * key. It is git-ignored and absent in most places. A missing file, or a host that answers an
   * unknown path with the app's home page, gives nothing and leaves the main file in charge.
   */
  private async loadLocal(url: string): Promise<object> {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return {};
      const body: unknown = await response.json();
      return typeof body === 'object' && body !== null ? body : {};
    } catch {
      return {};
    }
  }
}

/** Registers the config load as an app initializer so it finishes before the first route. */
export function provideRuntimeConfig(): EnvironmentProviders {
  return provideAppInitializer(() => inject(RuntimeConfigService).load());
}
